import config from '@/config'
import { store } from '@/stores'
import { sessionTokenAtom } from '@/stores/session'
import { AUTHORIZATION_HEADER_PREFIX } from '@repo/constants'

export function downloadFile(fileName: string, blob: Blob) {
  // Create a download link and trigger the download
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  // Set the file name
  link.setAttribute('download', fileName)

  document.body.appendChild(link)
  link.click()

  // Clean up
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export async function fetchCsvExport(apiRoute: string, signal?: AbortSignal) {
  const url = new URL(apiRoute, config.backendURL)

  const sessionToken = store.get(sessionTokenAtom)

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `${AUTHORIZATION_HEADER_PREFIX}${sessionToken}`,
    },
    signal,
  })

  const csvExport = await parseCsvExportResponse(response)

  return csvExport
}

/**
 * Pulls a human-readable message out of a failed export response. The backend error handler returns
 * JSON (`{ message }`), but an infrastructure error (e.g. an ingress "503 Service Temporarily
 * Unavailable" page shown when a synchronous export overwhelms the backend or exceeds the proxy
 * timeout) is an HTML body — fall back to the status line in that case.
 */
async function extractExportErrorMessage(response: Response): Promise<string> {
  const fallback = `Export failed: ${response.status} ${response.statusText}`.trim()

  if ((response.headers.get('Content-Type') || '').includes('application/json')) {
    try {
      const body = (await response.json()) as { message?: string; error?: string }
      return body.message || body.error || fallback
    } catch {
      // Malformed JSON body — fall through to the generic status message.
    }
  }

  return fallback
}

async function parseCsvExportResponse(csvExportResponse: Response) {
  // A non-2xx response is never a real export: it's either a backend error (JSON) or an
  // infrastructure error page (HTML). Reading its body and saving it as a file produces a corrupt
  // download — e.g. a "503 Service Temporarily Unavailable" HTML page saved as `export.zip` that the
  // user can't open. Throw so callers surface the failure instead of silently downloading garbage.
  if (!csvExportResponse.ok) {
    throw new Error(await extractExportErrorMessage(csvExportResponse))
  }

  const contentType = csvExportResponse.headers.get('Content-Type') || ''
  const disposition = csvExportResponse.headers.get('Content-Disposition') || ''

  const filenameMatch = disposition.match(/filename="?([^"]+)"?/)

  // A successful export always carries a Content-Disposition filename; the fallbacks only guard a
  // missing header and derive the extension from the content type. Never default to `.zip`, which
  // previously masked error responses as unopenable "zip" files.
  const filename = filenameMatch
    ? filenameMatch[1]
    : contentType.includes('zip')
      ? 'export.zip'
      : 'export.csv'

  // Count of rows flagged with an invalid (over-1M per-unit) value; absent header ⇒ 0.
  const invalidCount = Number(csvExportResponse.headers.get('X-Export-Invalid-Count') ?? 0)

  // A code the caller turns into its own translated warning; absent header ⇒ none.
  const warningCode = csvExportResponse.headers.get('X-Export-Warning') ?? undefined

  const blob = await csvExportResponse.blob()

  return { blob, filename, invalidCount, warningCode }
}

export async function downloadAuditTrailsFile(objectName: string, blob: Blob) {
  downloadFile(`${objectName}-export.json`, blob)
}
